/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'jquery',
	'../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./CanvasAPISpec',
	'../api/CanvasAPI',
	'underscore',
	'../dashboard/util/PropertyLayoutHelper'
], ($, APIFactory, CanvasAPISpec, CanvasAPI, _, PropertyLayoutHelper) => {

	const DEFAULT_LAYOUT_TYPES = ['widget', 'group'];

	class CanvasAPIImpl extends CanvasAPISpec{
		constructor(options) {
			super(options);
			this.boardModel = options.boardModel;
			this.extensions = options.extensions;

			this.contentFeatureCollection = options.contentFeatureCollection;
			// TODO - add inline feature here

			this.dashboardFeatures = options.dashboardFeatures;

			this.logger = this.dashboardFeatures.getFeature('Logger');

			this.contentFactory = this.dashboardFeatures.getFeature('ContentFactory');
			this._dashboard = this.dashboardFeatures.getFeature('API');

			this.canvasContent = this.contentFactory.createContent({
				dashboardFeatures: this.dashboardFeatures,
				contentFeatureCollection: this.contentFeatureCollection,
				boardModel: this.boardModel,
				layoutModel: this.boardModel.layout,
				canvas: this,
				profile: options.profile,
				type: this.boardModel.layout.type
			});

			this.copyCallback = options.copyCallback;
			this.pasteCallback = options.pasteCallback;

			this._createDashboardContent();


			APIFactory.setParentChildRelation(this, this.canvasContent);
		}

		_createDashboardContent() {
			const options = {
				featureLoader: this.dashboardFeatures,
				dashboardFeatures: this.dashboardFeatures,
				boardModel: this.boardModel,
				type: 'dashboard'
			};
			const contentFactory = this.dashboardFeatures.getFeature('ContentFactory');
			this._dashboardContent = contentFactory.createContent(options);
		}


		getRootContentInstance(){
			return this.canvasContent;
		}

		/**
		 * @param {*} contentSpecType the type of content.spec
		 * @return {booelan} if the spec is a layoutSpec(e.g. tab, group, ...)
		 */
		isLayoutType(contentSpecType) {
			const layoutViewExtentions = this.extensions.getLayoutViewExtensions();
			const authoringViewsLayoutTypeList = Object.keys(layoutViewExtentions.authoringViews);
			const consumeViewsLayoutTypeList = Object.keys(layoutViewExtentions.consumeViews);
			return authoringViewsLayoutTypeList.indexOf(contentSpecType) !== -1 || consumeViewsLayoutTypeList.indexOf(contentSpecType) !== -1;
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [CanvasAPI]);
			}
			return this.api;
		}

		initializeContentFeatures(contentFeatureCollection) {
			this.canvasContent.setContentFeatures(contentFeatureCollection);
			return Promise.all([this._dashboardContent.initialize(), this.canvasContent.initialize()]).then(() => {
				this._contentTargets = [this._dashboardContent, this.canvasContent];
			});
		}

		destroy() {
			this.boardModel = null;
			this.canvasContent.destroy();
			this.canvasContent = null;
			this.dashboardFeatures = null;
			this.api = null;
			this._dashboardContent.destroy();
			this._dashboardContent = null;
		}

		getContentActionList(idList) {
			if (idList.length) {
				return this.dashboardFeatures.getFeature('ContentActions').getContentActionList(idList);
			}
			return [];
		}

		copy() {
			// TODO - clean this up
			return this.copyCallback.apply(null, arguments);
		}
		paste() {
			// TODO - clean this up
			return this.pasteCallback.apply(null, arguments);
		}

		addContent(options, transactionToken) {
			const container = this.getContentInstance(options.containerId);
			return container.getAPI().addContent(options, transactionToken);
		}

		moveContent(containerId, contentIdList, transactionToken, insertBeforeMap) {
			const container = this.getContentInstance(containerId);
			return container.moveContent(containerId, contentIdList, transactionToken, insertBeforeMap);
		}

		removeContent(id, transactionToken) {
			const content = this.getContent(id);
			if (content) {
				const container = content.getContainer();
				if (container) {
					container.removeContent(id, transactionToken);
				}
			}
		}

		getContentInstance (contentId) {
			let content;
			if (!contentId || contentId === this.boardModel.layout.id) {
				content = this.canvasContent;
			}else {
				content = this.canvasContent.getContentInstance(contentId);
			}
			return content;
		}

		registerContentFeatures (contentId, features) {
			const content = this.getContentInstance(contentId);
			if (content){
				return content.registerFeatures(features);
			}
			return Promise.reject('The content with id "'+contentId+ '" does not exist.');
		}

		registerContentFeature(contentId, featureId, featureInstance, featureSpec, skipInitialization) {
			const content = this.getContentInstance(contentId);
			if (content){
				return content.registerFeature(featureId, featureInstance, featureSpec, skipInitialization);
			}
			return Promise.reject('The content with id "'+contentId+ '" does not exist.');
		}

		getContentFeature(contentId, featureId) {
			if (contentId === this.boardModel.layout.id) {
				return this.canvasContent.getFeature(featureId);
			}
			const content = this.canvasContent.getContentInstance(contentId);
			if (content) {
				return content.getFeature(featureId);
			}
		}

		getContent(id) {
			// Root layout
			if (!id || id === this.boardModel.layout.id) {
				return this.canvasContent.getAPI();
			}

			return this.canvasContent.getContent(id);
		}

		findContent(selector) {
			// if the type matches the top layout then return it..
			if (selector && selector.type === this.boardModel.layout.type) {
				return [this.canvasContent.getAPI()];
			}
			return this.canvasContent.findContent(selector);
		}

		selectContent(idList = []) {
			idList.forEach((id) => {
				const model = this.boardModel.layout.findModel(id);
				if (model) {
					const parent = model.getParent();
					if (parent) {
						parent.select(model.id);
					}
				}

			});
		}

		deselectContent(idList = []) {
			idList.forEach((id) => {
				const model = this.boardModel.layout.findModel(id);
				if (model) {
					const parent = model.getParent();
					if (parent) {
						parent.deselect(model.id);
					}
				}

			});
		}

		getSelectedContentList(selector = {}) {
			let selectContentList = [];
			if(this.boardModel){
				const contentRegistry = this.dashboardFeatures.getFeature('ContentTypeRegistry');
				const types = selector.type ? [selector.type] : DEFAULT_LAYOUT_TYPES;
				const selectedChildLayouts = this.boardModel.layout.getSelectedChildLayouts();
				const selectedFilteredChildLayouts = selectedChildLayouts.filter(layout => {
					const content = this.getContent(layout.id);
					let result = false;
					if (contentRegistry) {
						result = contentRegistry.isTypeRegistered(layout.type);
					}
					if (!result) {
						result = types.includes(layout.type) || types.includes(content.getType());
					}
					return result;
				});
				const parents = _.filter(selectedFilteredChildLayouts, layout => _.isArray(layout.items));
				const selectedLayouts = _.filter(selectedFilteredChildLayouts, layout => {
					// remove layouts that are part of selected parents
					return !_.find(parents, (parent) => parent.findChildItem(parent.items, layout.id));
				});
				selectContentList = _.map(selectedLayouts, layout => this.getContent(layout.id));
			}
			return selectContentList;
		}


		getPropertyNameList() {
			return Object.keys(this._getPropertyMap());
		}

		setPropertyValue(propertyName, propertyValue, transactionToken) {
			const property = this._getProperty(propertyName);

			if (property.validatePropertyValue) {
				const validInfo = property.validatePropertyValue(propertyValue) || {};
				if (!validInfo.isValid) {
					this.logger.error(validInfo.message, propertyName, propertyValue);
					return;
				}
			}

			property.setPropertyValue(propertyValue, transactionToken);
		}

		getPropertyValue(propertyName) {
			const property = this._getProperty(propertyName);
			return property.getPropertyValue();
		}

		/**
		 * Returns fully expanded layout specification.  Called when rendering the property panel
		 *
		 * When PropertiesFeatureV2 is enabled, returns a fully expanded layout specification for the dashboard content
		 *  @example the expandedList groups all of the layout by the section Id
		 *
		 *  unexpandedLayoutList (layout list before merging) = [{
		 * 	{
		 * 		editable: false,
		 * 		id: "banner",
		 * 		type: "Banner",
		 * 		value: "Dashboard properties"
		 * 	},
		 *  {
		 * 		id: "advanced",
		 * 		label: "Advanced",
		 * 		type: "Section"
		 *  },
		 *  {
		 * 		id:'fredIsRed',
		 * 		label: 'Color consistency',
		 * 		type: 'ToggleButton'
		 * 	},
		 * 	{
		 * 		id: 'language_Default',
		 * 		type: 'SingleLineLink'
		 * 	}
		 * ]
		 *
		 * propertyList (before merging) = [{
		 * 	{
		 * 		editor: { position: 1, sectionId: "colorsAndTheme", uiControl: {type: "DropDown", label: "Visual theme", getOptions: xx}},
		 * 		id: ""theme"",
		 * 		getPropertyValue: function,
		 * 		setPropertyValue: function
		 * 	},
		 *  {
		 * 		id: "dataCache",
		 * 		editor: {..},
		 * 		getPropertyValue: function,
		 * 		setPropertyValue: function
		 *  }, .. etc
		 * ]
		 *
		 * returns expandedList (merged between propertyLayoutList and propertyList)
		 * [
		 * 	{
		 * 		editable: false,
		 * 		id: "banner",
		 * 		type: "Banner",
		 * 		value: "Dashboard properties"
		 * 	},
		 *  {
		 * 		id: "advanced",
		 * 		label: "Advanced",
		 * 		type: "Section",
		 * 		items: [
		 * 			{
		 * 				id:'fredIsRed',
		 * 				label: 'Color consistency',
		 * 				type: 'ToggleButton',
		 * 				readOnly: false
		 * 			},
		 * 			{
		 * 				id: 'dataCache',
		 * 			 	label: "Data cache",
		 * 				readOnly: false
		 * 				type: "DropDown",
		 * 				options: {
		 * 					{label: "Automatic", value: "automatic"},
		 * 					{label: "On", value: "yes"},
		 * 					{label: "No cache", value: "no"}
		 * 			},
		 * 			.. etc]
		 * 	}
		 * ]
		 */
		getPropertyLayoutList() {
			const featureChecker = this._dashboard.getGlassCoreSvc('.FeatureChecker');
			const isPropertiesV2Enabled = featureChecker && featureChecker.checkValue('dashboard', 'PropertiesV2', 'enabled');
			if(isPropertiesV2Enabled) {
				const layoutPropertiesForDashboardContent = this._dashboardContent.getFeature('Properties').getPropertyLayoutList();
				const propertiesList = this._dashboardContent.getFeature('Properties').getPropertyList();
				return PropertyLayoutHelper.getExpandedLayoutList(layoutPropertiesForDashboardContent, propertiesList);
			} else {
				const allLayouts = {};
				for (let i = 0; i < this._contentTargets.length; i++) {
					const target = this._contentTargets[i];
					const targetProperties = target.getFeature('Properties');
					const layouts = targetProperties.getPropertyLayoutList();
					for (let j = 0; j < layouts.length; j++) {
						const layout = layouts[j];
						if (allLayouts[layout.id]) {
							$.extend(true, allLayouts[layout.id], layout);
						} else {
							allLayouts[layout.id] = layout;
						}
					}
				}
				return PropertyLayoutHelper.getExpandedLayoutList(_.values(allLayouts), this.getPropertyList());
			}
		}

		/* TODO: this function does not have to be exposed if we only feed expanded property layout into in the Properties v2 react view*/
		getPropertyList() {
			this._initProperties();
			return this._propertiesList;
		}

		_getPropertyMap() {
			this._initProperties();
			return this._propertiesMap;
		}

		/**
		 * Get the list of properties and caches them
		 */
		_initProperties() {
			this._propertiesMap = {};

			this._contentTargets = this._contentTargets || [];
			for( let i = 0; i < this._contentTargets.length; i++) {
				const target = this._contentTargets[i];
				const properties = target.getPropertyList() || [];
				for( let j=0; j < properties.length; j++) {
					const property = properties[j];
					if (this._propertiesMap[property.id]) {
						$.extend(true, this._propertiesMap[property.id], property);
					} else {
						this._propertiesMap[property.id] = property;
					}
				}
			}


			this._propertiesList = _.values(this._propertiesMap);
		}

		// TODO : we should define a way to get property using cache, currently,
		// every time we call _getProperty, it will init a new _propertiesMap again
		// it does not really using cache
		_getProperty(propertyName) {
			const property = this._getPropertyMap()[propertyName];
			if (!property) {
				throw new Error(`Property ${propertyName} does not exist.`);
			}

			return property;
		}

		whenContentReady(contentId) {
			// find content and call intialize
			const contentImpl = this.getContentInstance(contentId);
			return contentImpl ? contentImpl.initialize() : Promise.reject(`Could not find content with id ${contentId}`);
		}
	}

	return CanvasAPIImpl;
});
